import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import Notification from '@rio-cloud/rio-uikit/Notification';
import { type ChangeEvent, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch } from '../../../../../configuration/setup/hooks';
import { type RevokeEulaRequest, revokeEulaField } from '../../../../app/types';
import { useRevokeManEulaStatusMutation } from '../../../../services/accountsApi';
import type { ManEulaStatus } from '../../../../services/schemas/accountsBackendCodec';
import { FormInputField } from '../FormInputField';
import { emailSchema, nameSchema, reasonSchema } from '../schema';
import { resetAccountsSidebar } from '../sidebar/accountSidebarSlice';

interface AccountEulaDeclineDialogProps {
    eulaStatus: ManEulaStatus;
}
type FormErrors = { [key: string]: string | undefined } | undefined;

export const RevokeEulaDialog = ({ eulaStatus }: AccountEulaDeclineDialogProps) => {
    const [showDialog, setShowDialog] = useState(false);
    const [confirmationText, setConfirmationText] = useState('');

    const [eulaToRevoke, setEulaToRevoke] = useState({} as RevokeEulaRequest);
    const [formError, setFormError] = useState<FormErrors>({});

    useEffect(() => {
        setFormError({
            [revokeEulaField.FIRST_NAME]:
                eulaToRevoke.firstname !== undefined && nameSchema.validate(eulaToRevoke.firstname).error
                    ? 'helpgang.addFleetAdminDialog.name.error'
                    : undefined,
            [revokeEulaField.LAST_NAME]:
                eulaToRevoke.lastname !== undefined && nameSchema.validate(eulaToRevoke.lastname).error
                    ? 'helpgang.addFleetAdminDialog.name.error'
                    : undefined,
            [revokeEulaField.EMAIL]:
                eulaToRevoke.email !== undefined && emailSchema.validate(eulaToRevoke.email).error
                    ? 'helpgang.addFleetAdminDialog.email.error'
                    : undefined,
            [revokeEulaField.REASON]:
                eulaToRevoke.reason !== undefined &&
                eulaToRevoke.reason !== '' &&
                reasonSchema.validate(eulaToRevoke.reason).error
                    ? 'helpgang.addFleetAdminDialog.reason.error'
                    : undefined,
        });
    }, [eulaToRevoke]);

    const handlePropertyChange = (propertyName: string, value: string | boolean) => {
        setEulaToRevoke(prevState => ({
            ...prevState,
            [propertyName]: value,
        }));
    };

    const dispatch = useAppDispatch();
    const intl = useIntl();

    const [
        revokeEulaStatusRtk,
        {
            isLoading: revokeEulaStatusIsLoading,
            isSuccess: revokeEulaStatusIsSuccess,
            isError: revokeEulaStatusIsError,
            error: revokeEulaStatusError,
        },
    ] = useRevokeManEulaStatusMutation();

    useEffect(() => {
        if (revokeEulaStatusIsSuccess) {
            Notification.success(intl.formatMessage({ id: 'helpgang.notification.revokeEulaSuccess' }));
            dispatch(resetAccountsSidebar());
            setShowDialog(false);
        }
    }, [revokeEulaStatusIsSuccess, intl, dispatch]);

    useEffect(() => {
        if (revokeEulaStatusIsError) {
            console.error(`Could not revoke EULA: ${JSON.stringify(revokeEulaStatusError)}`);
            Notification.error(intl.formatMessage({ id: 'helpgang.notification.couldNotRevokeEula' }));
        }
    }, [revokeEulaStatusIsError, revokeEulaStatusError, intl]);

    const resetState = () => {
        setConfirmationText('');
        setEulaToRevoke({} as RevokeEulaRequest);
    };

    const VALID_CONFIRMATION_TEXT = 'REVOKE';
    const isConfirmationTextValid = () => VALID_CONFIRMATION_TEXT === confirmationText;

    const isNotEmpty = (s: string | null | undefined) => {
        return s != null && s.trim().length !== 0;
    };

    const isFormValid = () =>
        isConfirmationTextValid() &&
        isNotEmpty(eulaToRevoke.firstname) &&
        isNotEmpty(eulaToRevoke.lastname) &&
        isNotEmpty(eulaToRevoke.email) &&
        isNotEmpty(eulaToRevoke.reason);

    const handleConfirmationTextChange = (event: ChangeEvent<HTMLInputElement>) =>
        setConfirmationText(event.target.value);

    const getModalContent = () => {
        return (
            <div data-cy={'modal-content-change-lifecycle-state'}>
                <div>
                    <FormInputField
                        dataAttribute='add-fleet-admin-dialog-first-name-input'
                        translationId={'helpgang.addFleetAdminDialog.firstName'}
                        value={eulaToRevoke.firstname}
                        propertyName={revokeEulaField.FIRST_NAME}
                        // biome-ignore lint/complexity/useOptionalChain: suppressed out of convenience for migration to biomejs
                        formError={formError && formError[revokeEulaField.FIRST_NAME]}
                        autoFocus={true}
                        required={true}
                        onValueChange={handlePropertyChange}
                    />
                    <FormInputField
                        dataAttribute='add-fleet-admin-dialog-last-name-input'
                        translationId={'helpgang.addFleetAdminDialog.lastName'}
                        value={eulaToRevoke.lastname}
                        propertyName={revokeEulaField.LAST_NAME}
                        // biome-ignore lint/complexity/useOptionalChain: suppressed out of convenience for migration to biomejs
                        formError={formError && formError[revokeEulaField.LAST_NAME]}
                        required={true}
                        onValueChange={handlePropertyChange}
                    />
                    <FormInputField
                        dataAttribute='add-fleet-admin-dialog-email-input'
                        translationId={'helpgang.addFleetAdminDialog.email'}
                        placeholder={'max.mustermann@example.com'}
                        value={eulaToRevoke.email}
                        propertyName={revokeEulaField.EMAIL}
                        // biome-ignore lint/complexity/useOptionalChain: suppressed out of convenience for migration to biomejs
                        formError={formError && formError[revokeEulaField.EMAIL]}
                        required={true}
                        onValueChange={handlePropertyChange}
                    />

                    <FormInputField
                        dataAttribute='life-cycle-change-dialog-reason-input'
                        translationId={'helpgang.addFleetAdminDialog.reason'}
                        value={eulaToRevoke.reason}
                        propertyName={revokeEulaField.REASON}
                        formError={
                            eulaToRevoke.reason?.length === 0 ? 'helpgang.deleteUserDialog.reason.error' : undefined
                        }
                        required={true}
                        onValueChange={handlePropertyChange}
                        id={'reasonInputField'}
                    />
                </div>
                <p>
                    <FormattedMessage id={'helpgang.modal.typeConfirmation'} />:{' '}
                    <span className='text-bold text-color-danger pointer-events-none user-select-none'>
                        {VALID_CONFIRMATION_TEXT}
                    </span>
                </p>
                <div className='form-group'>
                    <input
                        className='form-control'
                        type='text'
                        value={confirmationText}
                        placeholder='Confirmation Input'
                        onChange={handleConfirmationTextChange}
                        data-cy={'update-account-locking-info-dialog-confirmation-input'}
                    />
                </div>
            </div>
        );
    };

    const onClose = () => {
        resetState();
        setShowDialog(false);
    };

    const handleConfirmButtonClick = () => {
        eulaToRevoke.reason && revokeEulaStatusRtk({ req: eulaToRevoke, eulaStatus });
    };

    return (
        <>
            <button
                type='button'
                className='btn btn-warning btn-outline'
                disabled={eulaStatus.status !== 'ACCEPTED'}
                onClick={_e => {
                    setShowDialog(true);
                }}
            >
                Revoke EULA
            </button>

            <ConfirmationDialog
                show={showDialog}
                title={'Revoke EULA'}
                content={getModalContent()}
                bsSize={'md'}
                onClickConfirm={handleConfirmButtonClick}
                onClickCancel={onClose}
                cancelButtonText={<FormattedMessage id={'helpgang.confirmationDialog.cancel'} />}
                confirmButtonText={'Revoke'}
                useOverflow={true}
                disableConfirm={!isFormValid() && !revokeEulaStatusIsLoading}
            />
        </>
    );
};
